import { useEffect, useState, useCallback } from "react";
import { Download, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function monthBounds(offset = 0) {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const start = new Date(d.getFullYear(), d.getMonth(), 1);
  const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const iso = (x) => x.toISOString().slice(0, 10);
  const today = iso(now);
  const endIso = iso(end);
  return { start: iso(start), end: endIso > today ? today : endIso };
}

const statusPill = (s) => {
  if (s === "refunded") return "bg-red-100 text-red-700";
  if (s === "partially_refunded") return "bg-amber-100 text-amber-700";
  return "bg-[#C6FF33] text-[#121212]";
};

export default function AdminSalesReport() {
  const init = monthBounds(0);
  const [start, setStart] = useState(init.start);
  const [end, setEnd] = useState(init.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/sales-report", { params: { start, end } });
      setData(data);
    } catch (e) {
      toast.error("Failed to load sales report");
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const setMonth = (offset) => {
    const b = monthBounds(offset);
    setStart(b.start);
    setEnd(b.end);
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/sales-report/export", { params: { start, end }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cake-society-sales-${start}-to-${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const printStatement = () => {
    if (!data) { toast.error("Nothing to print yet"); return; }
    const sm = data.summary || {};
    const fmtRange = `${start} to ${end}`;
    const generated = new Date().toLocaleString();
    const catRows = (data.by_category || []).map((c) => `
      <tr>
        <td>${c.category}</td>
        <td class="r">${money(c.gross_sales)}</td>
        <td class="r">${money(c.taxable_sales)}</td>
        <td class="r">${money(c.non_taxable_sales)}</td>
        <td class="r">${money(c.tax_collected)}</td>
        <td class="r">${money(c.refunds)}</td>
        <td class="r b">${money(c.net_sales)}</td>
      </tr>`).join("");
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Cake Society — Sales Statement</title>
      <style>
        @page { size: letter portrait; margin: 0.6in; }
        * { box-sizing: border-box; }
        body { font-family: Georgia, 'Times New Roman', serif; color: #121212; margin: 0; }
        .brand { font-weight: 800; letter-spacing: -0.5px; font-size: 26px; }
        .brand span { color: #7bb800; }
        .muted { color: #666; font-size: 12px; }
        h1 { font-size: 18px; margin: 18px 0 2px; }
        .hdr { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 3px solid #121212; padding-bottom: 10px; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 18px 0; }
        .card { border: 1px solid #ddd; border-radius: 8px; padding: 12px 14px; }
        .card .l { font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase; color: #777; }
        .card .v { font-size: 22px; font-weight: 800; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
        th, td { padding: 7px 8px; border-bottom: 1px solid #eee; text-align: left; }
        th { font-size: 10px; letter-spacing: 1px; text-transform: uppercase; color: #777; border-bottom: 2px solid #121212; }
        td.r, th.r { text-align: right; }
        td.b { font-weight: 800; }
        tfoot td { border-top: 2px solid #121212; font-weight: 800; }
        .foot { margin-top: 24px; font-size: 10px; color: #999; }
      </style></head><body>
      <div class="hdr">
        <div><div class="brand">Cake<span>Society</span></div><div class="muted">Sales &amp; Tax Statement</div></div>
        <div style="text-align:right"><div style="font-weight:700">${fmtRange}</div><div class="muted">Generated ${generated}</div></div>
      </div>
      <div class="grid">
        <div class="card"><div class="l">Gross Sales</div><div class="v">${money(sm.gross_sales)}</div></div>
        <div class="card"><div class="l">Taxable Sales</div><div class="v">${money(sm.taxable_sales)}</div></div>
        <div class="card"><div class="l">Non-Taxable Sales</div><div class="v">${money(sm.non_taxable_sales)}</div></div>
        <div class="card"><div class="l">Sales Tax Collected</div><div class="v">${money(sm.tax_collected)}</div></div>
        <div class="card"><div class="l">Refunds</div><div class="v">${money(sm.refunds)}</div></div>
        <div class="card"><div class="l">Net Sales</div><div class="v">${money(sm.net_sales)}</div></div>
      </div>
      <h1>Breakdown by category</h1>
      <table>
        <thead><tr><th>Category</th><th class="r">Gross</th><th class="r">Taxable</th><th class="r">Non-Taxable</th><th class="r">Tax</th><th class="r">Refunds</th><th class="r">Net</th></tr></thead>
        <tbody>${catRows || '<tr><td colspan="7">No sales in this period.</td></tr>'}</tbody>
        <tfoot><tr>
          <td>Total</td>
          <td class="r">${money(sm.gross_sales)}</td>
          <td class="r">${money(sm.taxable_sales)}</td>
          <td class="r">${money(sm.non_taxable_sales)}</td>
          <td class="r">${money(sm.tax_collected)}</td>
          <td class="r">${money(sm.refunds)}</td>
          <td class="r">${money(sm.net_sales)}</td>
        </tr></tfoot>
      </table>
      <div class="muted" style="margin-top:14px">${sm.transactions || 0} transaction(s) in this period. Full line-item detail is available via the CSV export.</div>
      <div class="foot">Cake Society · cakesocietystudio.com · Sales tax is collected as a pass-through liability and is not counted in net sales.</div>
      <script>window.onload = function(){ window.focus(); window.print(); };</script>
      </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to print the statement"); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  const s = data?.summary || {};
  const cards = [
    { l: "Gross sales", v: money(s.gross_sales), tone: "text-[#121212]" },
    { l: "Taxable sales", v: money(s.taxable_sales), tone: "text-[#121212]" },
    { l: "Non-taxable sales", v: money(s.non_taxable_sales), tone: "text-[#121212]" },
    { l: "Sales tax collected", v: money(s.tax_collected), tone: "text-[#0f766e]" },
    { l: "Refunds", v: money(s.refunds), tone: "text-red-600" },
    { l: "Net sales", v: money(s.net_sales), tone: "text-[#121212]" },
  ];

  return (
    <div className="mt-6 space-y-6" data-testid="admin-sales-report">
      {/* Filters */}
      <div className="p-5 rounded-2xl bg-white border-2 border-[#121212]">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">From</label>
            <input data-testid="sales-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="px-3 py-2 border-2 border-[#121212] focus:outline-none" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">To</label>
            <input data-testid="sales-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="px-3 py-2 border-2 border-[#121212] focus:outline-none" />
          </div>
          <button data-testid="sales-apply" onClick={load} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.2em] uppercase font-bold rounded-full">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Apply
          </button>
          <div className="flex gap-2">
            <button data-testid="sales-this-month" onClick={() => setMonth(0)} className="px-4 py-2.5 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#F2E9D8] rounded-full">This month</button>
            <button data-testid="sales-last-month" onClick={() => setMonth(-1)} className="px-4 py-2.5 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#F2E9D8] rounded-full">Last month</button>
          </div>
          <button data-testid="sales-pdf-statement" onClick={printStatement} className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 border-2 border-[#121212] text-[#121212] hover:bg-[#F2E9D8] transition-colors text-xs tracking-[0.2em] uppercase font-bold rounded-full">
            <FileText size={15} /> PDF Statement
          </button>
          <button data-testid="sales-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C6FF33] text-[#121212] border-2 border-[#121212] hover:bg-[#121212] hover:text-[#C6FF33] transition-colors text-xs tracking-[0.2em] uppercase font-bold rounded-full">
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sales-summary">
        {cards.map((c) => (
          <div key={c.l} className="p-5 rounded-2xl bg-white border border-stone-200">
            <p className="text-xs tracking-[0.2em] uppercase text-stone-500">{c.l}</p>
            <p className={`font-serif font-black text-3xl mt-2 ${c.tone}`}>{c.v}</p>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-200">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-500">Breakdown by category</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-category-table">
            <thead>
              <tr className="text-left text-stone-500 text-xs tracking-[0.12em] uppercase border-b border-stone-100">
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Gross</th>
                <th className="px-4 py-3 text-right">Taxable</th>
                <th className="px-4 py-3 text-right">Non-taxable</th>
                <th className="px-4 py-3 text-right">Tax</th>
                <th className="px-4 py-3 text-right">Refunds</th>
                <th className="px-4 py-3 text-right">Net</th>
                <th className="px-4 py-3 text-right">#</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_category || []).length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-stone-500">No sales in this date range yet.</td></tr>
              ) : (data.by_category.map((c) => (
                <tr key={c.category} data-testid={`sales-cat-${c.category}`} className="border-b border-stone-50">
                  <td className="px-4 py-3 font-medium text-stone-900">{c.category}</td>
                  <td className="px-4 py-3 text-right">{money(c.gross_sales)}</td>
                  <td className="px-4 py-3 text-right">{money(c.taxable_sales)}</td>
                  <td className="px-4 py-3 text-right">{money(c.non_taxable_sales)}</td>
                  <td className="px-4 py-3 text-right text-[#0f766e]">{money(c.tax_collected)}</td>
                  <td className="px-4 py-3 text-right text-red-600">{money(c.refunds)}</td>
                  <td className="px-4 py-3 text-right font-bold">{money(c.net_sales)}</td>
                  <td className="px-4 py-3 text-right text-stone-500">{c.transactions}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transactions */}
      <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-500">Transactions</p>
          <p className="text-xs text-stone-400">{(data?.transactions || []).length} rows</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sales-transactions-table">
            <thead>
              <tr className="text-left text-stone-500 text-xs tracking-[0.12em] uppercase border-b border-stone-100">
                <th className="px-4 py-3">Paid</th>
                <th className="px-4 py-3">Event</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Item</th>
                <th className="px-4 py-3 text-right">Subtotal</th>
                <th className="px-4 py-3 text-right">Tax</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.transactions || []).length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-6 text-center text-stone-500">No transactions in this date range.</td></tr>
              ) : (data.transactions.map((r, i) => (
                <tr key={i} data-testid={`sales-txn-${i}`} className="border-b border-stone-50 align-top">
                  <td className="px-4 py-3 whitespace-nowrap">{r.payment_date}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-stone-500">{r.event_date || "—"}</td>
                  <td className="px-4 py-3 max-w-[160px] truncate" title={r.customer_name}>{r.customer_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.category}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-stone-600">{r.transaction_type}</td>
                  <td className="px-4 py-3 max-w-[220px] truncate" title={r.item_name}>{r.item_name || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{money(r.subtotal)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap text-[#0f766e]">{money(r.tax_amount)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap font-medium">{money(r.total)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[11px] font-bold tracking-[0.08em] uppercase ${statusPill(r.status)}`}>{r.status.replace(/_/g, " ")}</span>
                    {r.refunded_amount > 0 && <span className="block text-[11px] text-red-500 mt-1">−{money(r.refunded_amount)}</span>}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
